/**
 * Utility functions for audit sidebar field name mapping
 */

type FieldKey = string;
type DisplayName = string;

/**
 * Mapping of tenant field keys to their display names
 */
const TENANT_FIELD_MAP: Record<FieldKey, DisplayName> = {
  name: "Name",
  account_manager: "Account Manager",
  given_name: "Given Name",
  surname: "Surname",
  country: "Country",
  "Customer Admin given_name": "Customer Admin Given Name",
  "Customer Admin surname": "Customer Admin Surname",
  "Customer Admin country": "Customer Admin Country",
  "Customer Admin Tenant": "Customer Admin Tenant",
  entity_id: "Entity ID",
  entity_name: "Entity Name",
  expires_at: "Expiration Date",
  expiresAt: "Expiration Date",
  services: "Services",
  user_id: "User ID",
  user_name: "User Name",
  user_email: "User Email",
  assigned_tenants: "Assigned Tenants",
};

/**
 * Checks if a field key is a tenant field (exists in the mapping)
 *
 * @param key - The field key to check
 * @returns True if the key is in the tenant field map
 */
export function isTenantField(key: FieldKey): boolean {
  return key in TENANT_FIELD_MAP;
}

/**
 * Formats a field name by splitting on underscores and capitalizing each word.
 * Pure function with no side effects.
 *
 * @param key - The field key to format (e.g., "salesforce_id")
 * @returns Formatted display name (e.g., "Salesforce Id")
 */
function formatFieldNameFallback(key: FieldKey): DisplayName {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Gets the display name for a tenant field.
 * Checks the mapping first, then falls back to formatting the key.
 * Pure function with no side effects.
 *
 * @param key - The field key to get display name for
 * @returns Human-readable display name
 */
export function getTenantFieldDisplayName(key: FieldKey): DisplayName {
  if (TENANT_FIELD_MAP[key]) {
    return TENANT_FIELD_MAP[key];
  }
  return formatFieldNameFallback(key);
}
